import Swiper from 'swiper';
import { Autoplay, Pagination } from 'swiper/modules';

document.addEventListener('DOMContentLoaded', function () {
    const blockMediaSliders = document.querySelectorAll('.block-media__slider');

    if (blockMediaSliders.length === 0) return;

    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
        gsap.registerPlugin(ScrollTrigger);
    }

    blockMediaSliders.forEach(function (slider) {
        const slides      = slider.querySelectorAll('.swiper-slide');
        const pauseButton = slider.querySelector('.block-media__pause');
        const inner       = slider.closest('.block-media__inner');
        let swiper        = null;

        updateBlockMediaBleed(slider, inner);

        animateBlockMediaReveal(slider, inner);
        animateBlockMediaEmbedReveal(slider, inner);

        if (slides.length > 1) {
            swiper = new Swiper(slider, {
                modules: [Autoplay, Pagination],
                slidesPerView: 1,
                loop: true,
                speed: 700,
                autoplay: {
                    delay: 5000,
                    disableOnInteraction: false,
                },
                pagination: {
                    el: slider.querySelector('.swiper-pagination'),
                    type: 'bullets',
                    clickable: true,
                },
                observer: true,
                observeParents: true,
            });
        }

        slider.addEventListener('blockMediaRefresh', function () {
            updateBlockMediaBleed(slider, inner);

            if (swiper) {
                swiper.update();
            }
        });

        if (!pauseButton || !swiper) return;

        pauseButton.addEventListener('click', function () {
            const isPaused = pauseButton.classList.toggle('is-paused');

            if (isPaused) {
                swiper.autoplay.stop();
                pauseButton.setAttribute('aria-label', pauseButton.dataset.labelPlay || 'Play media carousel');
            } else {
                swiper.autoplay.start();
                pauseButton.setAttribute('aria-label', pauseButton.dataset.labelPause || 'Pause media carousel');
            }
        });
    });

    window.addEventListener('resize', function () {
        blockMediaSliders.forEach(function (slider) {
            updateBlockMediaBleed(slider, slider.closest('.block-media__inner'));
        });
    });

    document.addEventListener('productTabPanelShown', function (event) {
        window.requestAnimationFrame(function () {
            window.requestAnimationFrame(function () {
                event.target.querySelectorAll('.block-media__slider').forEach(function (slider) {
                    slider.dispatchEvent(new CustomEvent('blockMediaRefresh'));
                });

                if (typeof ScrollTrigger !== 'undefined') {
                    ScrollTrigger.refresh();
                }
            });
        });
    });
});

function updateBlockMediaBleed(slider, inner) {
    if (!slider || !inner) return;

    if (inner.getBoundingClientRect().width === 0) return;

    if (isBlockMediaMobile()) {
        resetBlockMediaMobile(slider);
        return;
    }

    const blockMediaBleed = getBlockMediaBleed(inner);
    const controls = slider.querySelector('.block-media__controls');
    const maxBleed = Math.max(blockMediaBleed.left, blockMediaBleed.right);

    slider.style.width = `calc(100% + ${blockMediaBleed.left + blockMediaBleed.right}px)`;
    slider.style.marginLeft  = `${blockMediaBleed.left * -1}px`;
    slider.style.marginRight = `${blockMediaBleed.right * -1}px`;

    if (controls) {
        controls.style.left  = `calc(${maxBleed}px + 2rem)`;
        controls.style.right = `calc(${maxBleed}px + 2rem)`;
    }

    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
        setBlockMediaReveal(slider, 0, 0);
    }
}

function animateBlockMediaReveal(slider, inner) {
    if (!slider || !inner) return;

    if (!slider.querySelector('.block-media__image, .block-media__video')) return;

    if (isBlockMediaMobile()) {
        setBlockMediaMediaReveal(slider, 0);
        return;
    }

    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
        setBlockMediaMediaReveal(slider, 0);
        return;
    }

    const blockMediaReveal = {
        right: 0,
    };

    const revealAnimation = gsap.fromTo(blockMediaReveal, {
        right: function () {
            return getBlockMediaBleed(inner).right;
        },
    }, {
        right: 0,
        duration: 1,
        ease: 'power3.out',
        paused: true,
        onUpdate: function () {
            setBlockMediaMediaReveal(slider, blockMediaReveal.right);
        },
    });

    ScrollTrigger.create({
        trigger: slider,
        start: 'top 80%',
        once: true,
        invalidateOnRefresh: true,
        onEnter: function () {
            revealAnimation.invalidate().restart();
        },
    });
}

function animateBlockMediaEmbedReveal(slider, inner) {
    if (!slider || !inner || !slider.querySelector('.block-media__embed')) return;

    if (isBlockMediaMobile()) {
        setBlockMediaEmbedReveal(slider, 0);
        return;
    }

    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
        setBlockMediaEmbedReveal(slider, 0);
        return;
    }

    const blockMediaReveal = {
        left: 0,
    };

    const revealAnimation = gsap.fromTo(blockMediaReveal, {
        left: function () {
            return getBlockMediaBleed(inner).left;
        },
    }, {
        left: 0,
        duration: 1,
        ease: 'power3.out',
        paused: true,
        onUpdate: function () {
            setBlockMediaEmbedReveal(slider, blockMediaReveal.left);
        },
    });

    ScrollTrigger.create({
        trigger: slider,
        start: 'top 80%',
        once: true,
        invalidateOnRefresh: true,
        onEnter: function () {
            revealAnimation.invalidate().restart();
        },
    });
}

function setBlockMediaReveal(slider, left, right) {
    setBlockMediaMediaReveal(slider, right);
    setBlockMediaEmbedReveal(slider, left);
}

function setBlockMediaMediaReveal(slider, right) {
    slider.querySelectorAll('.block-media__image, .block-media__video').forEach(function (media) {
        const clipPath = `inset(0px ${right}px 0px 0px)`;

        media.style.clipPath = clipPath;
        media.style.webkitClipPath = clipPath;
    });
}

function setBlockMediaEmbedReveal(slider, left) {
    slider.querySelectorAll('.block-media__embed').forEach(function (media) {
        const clipPath = `inset(0px 0px 0px ${left}px)`;

        media.style.clipPath = clipPath;
        media.style.webkitClipPath = clipPath;
    });
}

function getBlockMediaBleed(inner) {
    const innerRect = inner.getBoundingClientRect();

    if (innerRect.width === 0) {
        return {
            left: 0,
            right: 0,
        };
    }

    const leftBleed = Math.max(innerRect.left, 0);
    const rightBleed = Math.max(getBlockMediaViewportWidth() - innerRect.right, 0);

    return {
        left: leftBleed,
        right: rightBleed,
    };
}

function getBlockMediaViewportWidth() {
    return document.documentElement.clientWidth || window.innerWidth;
}

function resetBlockMediaMobile(slider) {
    const controls = slider.querySelector('.block-media__controls');

    slider.style.width = '';
    slider.style.marginLeft = '';
    slider.style.marginRight = '';

    if (controls) {
        controls.style.left = '';
        controls.style.right = '';
    }

    setBlockMediaReveal(slider, 0, 0);
}

function isBlockMediaMobile() {
    return window.matchMedia('(max-width: 767px)').matches;
}
